import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { zodResolver } from "@hookform/resolvers/zod";

import { cn } from "@/libs/cn";
import { Button, FileInput, Input, Modal, ModalBody, ModalFooter, SelectV2 } from "@/modules/common/components";
import { inputClassNameValues } from "@/modules/common/components/files/Input";
import { LEAD_ORIGIN_FORM_OPTIONS, LEAD_SEGMENT_OPTIONS, LeadOrigin, LeadStatus } from "@/modules/common/types/Leads.enum";
import { getApiErrorMessage } from "@/modules/common/utils/apiError";
import { digitsOnly } from "@/modules/common/utils/format";
import { uploadFiles } from "@/modules/common/utils/upload";

import useCreateLead from "../../hooks/useCreateLead";
import { LeadFormData, leadSchema } from "../../schemas";

const ACCEPTED_FILES = "image/*,.pdf,.doc,.docx,.xls,.xlsx";

interface LeadFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_VALUES: LeadFormData = {
  name: "",
  company: "",
  email: "",
  phoneNumber: "",
  segment: "",
  originType: LeadOrigin.MANUAL,
  interest: "",
};

const LeadFormModal = ({ open, onOpenChange }: LeadFormModalProps) => {
  const createLead = useCreateLead({
    onSuccess: () => {
      toast.success("Lead cadastrado com sucesso!");
      handleClose();
    },
  });
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const isSaving = createLead.isPending || isUploading;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LeadFormData>({ resolver: zodResolver(leadSchema), defaultValues: EMPTY_VALUES });

  const handleClose = () => {
    onOpenChange(false);
    reset(EMPTY_VALUES);
    setFiles([]);
  };

  const onSubmit = async (values: LeadFormData) => {
    let attachmentIds: string[] = [];

    if (files.length) {
      setIsUploading(true);
      setUploadedCount(0);

      try {
        attachmentIds = await uploadFiles(files, { onProgress: setUploadedCount });
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Erro ao enviar a fatura. Tente novamente."));
        return;
      } finally {
        setIsUploading(false);
      }
    }

    await createLead.mutateAsync({
      name: values.name,
      company: values.company,
      email: values.email,
      phoneNumber: values.phoneNumber ? digitsOnly(values.phoneNumber) : undefined,
      segment: values.segment || undefined,
      interest: values.interest || undefined,
      originType: values.originType,
      status: LeadStatus.IN_CONTACT,
      attachmentIds: attachmentIds.length ? attachmentIds : undefined,
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && !isSaving && handleClose()}
      title="Novo lead"
      description="Cadastre manualmente um lead recebido por outro canal."
      className="my-[5vh] flex max-h-[90vh] max-w-150 flex-col p-5 sm:p-8"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="mt-4 min-h-0 flex-1 gap-4 overflow-y-auto px-1 pb-1">
          <div>
            <Input label="Nome" markAsRequired {...register("name")} />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
          </div>

          <div>
            <Input label="Empresa" markAsRequired {...register("company")} />
            {errors.company && <p className="mt-1 text-xs text-red-500">{errors.company.message}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Input label="E-mail" markAsRequired maskType="email" {...register("email")} />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <Input label="Telefone / WhatsApp" maskType="tel" {...register("phoneNumber")} />
              {errors.phoneNumber && <p className="mt-1 text-xs text-red-500">{errors.phoneNumber.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="segment"
              control={control}
              render={({ field }) => (
                <SelectV2
                  mode="single"
                  label="Segmento"
                  placeholder="Selecione o segmento"
                  options={LEAD_SEGMENT_OPTIONS}
                  value={field.value || undefined}
                  onChange={(next) => field.onChange(next ?? "")}
                />
              )}
            />

            <div>
              <Controller
                name="originType"
                control={control}
                render={({ field }) => (
                  <SelectV2
                    mode="single"
                    label="Origem"
                    markAsRequired
                    placeholder="Selecione a origem"
                    options={LEAD_ORIGIN_FORM_OPTIONS}
                    value={field.value || undefined}
                    onChange={(next) => field.onChange(next ?? "")}
                  />
                )}
              />
              {errors.originType && <p className="mt-1 text-xs text-red-500">{errors.originType.message}</p>}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm">Necessidade informada</label>
            <textarea
              rows={3}
              placeholder="O que a empresa informou ou precisa..."
              className={cn(inputClassNameValues, "h-auto resize-y")}
              {...register("interest")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm">Fatura anexada</label>
            <FileInput
              title="Clique para anexar a fatura"
              files={files}
              onChange={setFiles}
              accept={ACCEPTED_FILES}
              multiple
              disabled={isSaving}
            />
          </div>
        </ModalBody>

        <ModalFooter className="mt-4 flex-col-reverse sm:mt-6 sm:flex-row">
          <Button type="button" variant="outlined" label="Cancelar" className="w-full" onClick={handleClose} disabled={isSaving} />
          <Button
            type="submit"
            label={isUploading ? `Enviando arquivos (${uploadedCount}/${files.length})` : "Salvar lead"}
            className="w-full"
            loading={isSaving}
            disabled={isSaving}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default LeadFormModal;
